import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  activateIntegration,
  deactivateIntegration,
  getIntegrations,
  updateIntegration,
} from '../../api';
import { PageHeader, StatusBadge, TableSkeleton } from '../../components/ui';
import { Plug, Save, Power, PowerOff } from 'lucide-react';

const CATEGORY_LABEL: Record<string, string> = {
  messaging: 'Messaging',
  realtime: 'Realtime / Calls',
  push: 'Push notifications',
  auth: 'OAuth',
  media: 'Media storage',
  support: 'Support',
};

/** Removed-scope integrations (AI, palm tools, mall geocode) — hide from admin UI */
const HIDDEN_INTEGRATION_CATEGORIES = new Set(['ai', 'astrology', 'maps']);
const HIDDEN_INTEGRATION_CODES = new Set([
  'ai_llm', 'openai', 'ai-astrologer', 'astrologyapi_palm', 'geocoder',
]);

const FIELD_META: Record<string, { label: string; secret?: boolean; type?: string }> = {
  provider: { label: 'Provider' },
  apiKey: { label: 'API key', secret: true },
  apiSecret: { label: 'API secret', secret: true },
  senderId: { label: 'Sender ID' },
  templateId: { label: 'Template ID' },
  from: { label: 'From' },
  testMode: { label: 'Test mode', type: 'boolean' },
  staticOtp: { label: 'Static OTP (test)' },
  host: { label: 'SMTP host' },
  port: { label: 'SMTP port', type: 'number' },
  user: { label: 'SMTP user' },
  pass: { label: 'SMTP password', secret: true },
  fromEmail: { label: 'From email' },
  fromName: { label: 'From name' },
  appId: { label: 'App ID' },
  appSign: { label: 'App Sign', secret: true },
  serverSecret: { label: 'Server secret', secret: true },
  baseUrl: { label: 'Base URL' },
  unlockPaise: { label: 'Unlock price (paise)', type: 'number' },
  dailyLimit: { label: 'Daily limit', type: 'number' },
  userAgent: { label: 'User-Agent' },
  countryBias: { label: 'Country bias' },
  projectId: { label: 'Project ID' },
  serverKey: { label: 'Server key', secret: true },
  serviceAccountJson: { label: 'Service account JSON', secret: true },
  model: { label: 'Model' },
  temperature: { label: 'Temperature', type: 'number' },
  maxTokens: { label: 'Max tokens', type: 'number' },
  fallbackDemo: { label: 'Fallback to demo', type: 'boolean' },
  clientId: { label: 'Client ID' },
  clientSecret: { label: 'Client secret', secret: true },
  teamId: { label: 'Team ID' },
  keyId: { label: 'Key ID' },
  privateKey: { label: 'Private key', secret: true },
  cloudName: { label: 'Cloud name' },
  bucket: { label: 'S3 bucket' },
  region: { label: 'Region' },
  accessKey: { label: 'Access key', secret: true },
  secretKey: { label: 'Secret key', secret: true },
  phoneE164: { label: 'WhatsApp phone (E.164)' },
  defaultMessage: { label: 'Default message' },
};

export default function IntegrationsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: () => getIntegrations().then((r) => r.data.data),
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [form, setForm] = useState<any>(null);

  const integrations = useMemo(() => {
    const rows = data?.integrations || [];
    return rows.filter(
      (i: any) =>
        !HIDDEN_INTEGRATION_CATEGORIES.has(String(i.category || '')) &&
        !HIDDEN_INTEGRATION_CODES.has(String(i.code || '')),
    );
  }, [data?.integrations]);
  const current = useMemo(
    () => integrations.find((i: any) => i.code === selected) || null,
    [integrations, selected],
  );

  useEffect(() => {
    if (!selected && integrations[0]) setSelected(integrations[0].code);
  }, [integrations, selected]);

  useEffect(() => {
    if (!current) {
      setForm(null);
      return;
    }
    const cfg = { ...(current.config || {}) };
    // Clear masked secrets for edit placeholders
    Object.keys(cfg).forEach((k) => {
      if (typeof cfg[k] === 'string' && String(cfg[k]).includes('*')) cfg[k] = '';
      if (k.endsWith('Set')) delete cfg[k];
    });
    setForm({
      name: current.name,
      description: current.description || '',
      isEnabled: current.isEnabled,
      config: cfg,
    });
  }, [current]);

  const saveMut = useMutation({
    mutationFn: () => updateIntegration(selected!, {
      name: form.name,
      description: form.description,
      isEnabled: form.isEnabled,
      config: form.config,
    }),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const activateMut = useMutation({
    mutationFn: () => activateIntegration(selected!),
    onSuccess: (r) => {
      toast.success(r.data.message || 'Activated');
      qc.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Activate failed'),
  });

  const deactivateMut = useMutation({
    mutationFn: () => deactivateIntegration(selected!),
    onSuccess: () => {
      toast.success('Deactivated');
      qc.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Deactivate failed'),
  });

  const grouped = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const i of integrations) {
      const c = i.category || 'other';
      if (!map[c]) map[c] = [];
      map[c].push(i);
    }
    return map;
  }, [integrations]);

  if (isLoading) {
    return (
      <div className="p-6">
        <PageHeader title="Integrations" subtitle="Third-party APIs — keys, enable / disable" />
        <TableSkeleton rows={6} cols={3} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Integrations"
        subtitle="Manage SMS, ZEGO, email, FCM and other vendor keys. Payment gateways stay under Finance."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-3">
          {Object.entries(grouped).map(([cat, rows]) => (
            <div key={cat} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b">
                {CATEGORY_LABEL[cat] || cat}
              </div>
              <ul>
                {rows.map((row) => (
                  <li key={row.code}>
                    <button
                      type="button"
                      onClick={() => setSelected(row.code)}
                      className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 border-b last:border-0 ${
                        selected === row.code ? 'bg-primary-50 text-primary-800' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Plug size={14} className="shrink-0 text-gray-400" />
                        <span className="truncate font-medium">{row.name}</span>
                      </span>
                      <StatusBadge status={row.isActive ? 'active' : 'inactive'} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4">
          {!form || !current ? (
            <p className="text-sm text-gray-500">Select an integration</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{current.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{current.description}</p>
                  <p className="text-[11px] font-mono text-gray-400 mt-1">{current.code}</p>
                </div>
                <div className="flex gap-2">
                  {current.isActive ? (
                    <button
                      type="button"
                      disabled={deactivateMut.isPending}
                      onClick={() => deactivateMut.mutate()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700"
                    >
                      <PowerOff size={14} /> Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={activateMut.isPending}
                      onClick={() => activateMut.mutate()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                    >
                      <Power size={14} /> Activate
                    </button>
                  )}
                </div>
              </div>

              <label className="block text-xs text-gray-500">Display name
                <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={form.name || ''} onChange={(e) => setForm((p: any) => ({ ...p, name: e.target.value }))} />
              </label>
              <label className="block text-xs text-gray-500">Description
                <textarea className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={form.description || ''} onChange={(e) => setForm((p: any) => ({ ...p, description: e.target.value }))} />
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.isEnabled} onChange={(e) => setForm((p: any) => ({ ...p, isEnabled: e.target.checked }))} />
                Enabled in catalog
              </label>

              <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Configuration</p>
                {Object.keys(form.config || {}).length === 0 && (
                  <p className="text-xs text-gray-400">No config fields</p>
                )}
                {Object.entries(form.config || {}).map(([key, value]) => {
                  const meta = FIELD_META[key] || { label: key };
                  if (meta.type === 'boolean') {
                    return (
                      <label key={key} className="inline-flex items-center gap-2 text-sm w-full">
                        <input
                          type="checkbox"
                          checked={!!value}
                          onChange={(e) => setForm((p: any) => ({
                            ...p,
                            config: { ...p.config, [key]: e.target.checked },
                          }))}
                        />
                        {meta.label}
                      </label>
                    );
                  }
                  return (
                    <label key={key} className="block text-xs text-gray-500">
                      {meta.label}{meta.secret ? ' (leave blank to keep)' : ''}
                      <input
                        type={meta.secret ? 'password' : meta.type === 'number' ? 'number' : 'text'}
                        className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono"
                        value={value == null ? '' : String(value)}
                        placeholder={meta.secret ? '••••••••' : undefined}
                        onChange={(e) => setForm((p: any) => ({
                          ...p,
                          config: {
                            ...p.config,
                            [key]: meta.type === 'number' ? Number(e.target.value) : e.target.value,
                          },
                        }))}
                      />
                    </label>
                  );
                })}
              </div>

              <button
                type="button"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium"
              >
                <Save size={14} /> Save integration
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
